/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface TenantConfig {
  orgId: string;
  orgName: string;
  subdomain: string;
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  customDomain?: string;
  features: {
    publicJobBoard: boolean;
    careerPage: boolean;
    applicationForm: boolean;
    atsIntegration: boolean;
  };
}

interface TenantContextType {
  tenant: TenantConfig | null;
  isLoading: boolean;
  error: string | null;
  isTenantDomain: boolean;
  isMainDomain: boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

interface TenantProviderProps {
  children: ReactNode;
}

/**
 * Extract subdomain from hostname
 * Examples:
 * - acme.epeople.work → acme
 * - dev.epeople.work → dev (environment, not tenant)
 * - test.epeople.work → test (environment, not tenant)
 * - epeople.work → null (main domain)
 * - localhost:5173 → null (development)
 */
const extractSubdomain = (hostname: string): string | null => {
  // Development environments
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return null;
  }

  // Extract subdomain parts
  const parts = hostname.split('.');
  
  // Must have at least subdomain.domain.tld
  if (parts.length < 3) {
    return null;
  }

  const subdomain = parts[0];
  
  // Exclude environment subdomains
  const envSubdomains = ['dev', 'test', 'staging', 'prod', 'www'];
  if (envSubdomains.includes(subdomain)) {
    return null;
  }

  return subdomain;
};

/**
 * Determine domain type
 */
const getDomainType = (hostname: string): 'main' | 'tenant' | 'dev' => {
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return 'dev';
  }

  const subdomain = extractSubdomain(hostname);
  return subdomain ? 'tenant' : 'main';
};

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [tenant, setTenant] = useState<TenantConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadTenantConfig = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const hostname = window.location.hostname;
        const subdomain = extractSubdomain(hostname);

        if (!subdomain) {
          // Main domain or development - no tenant
          setTenant(null);
          setIsLoading(false);
          return;
        }

        // TODO: Replace with actual API call
        // const response = await fetch(`/api/tenants/${subdomain}`);
        // const data = await response.json();

        // Mock tenant config for development
        await new Promise(resolve => setTimeout(resolve, 300));
        
        const mockTenant: TenantConfig = {
          orgId: `org_${subdomain}`,
          orgName: subdomain.charAt(0).toUpperCase() + subdomain.slice(1) + ' Corporation',
          subdomain: subdomain,
          logoUrl: undefined,
          primaryColor: '#2563eb',
          secondaryColor: '#1e40af',
          features: {
            publicJobBoard: true,
            careerPage: true,
            applicationForm: true,
            atsIntegration: false,
          },
        };

        setTenant(mockTenant);
      } catch (err) {
        console.error('Failed to load tenant config:', err);
        setError(err instanceof Error ? err.message : 'Failed to load organization');
        setTenant(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadTenantConfig();
  }, []);

  const hostname = window.location.hostname;
  const domainType = getDomainType(hostname);

  const value: TenantContextType = {
    tenant,
    isLoading,
    error,
    isTenantDomain: domainType === 'tenant',
    isMainDomain: domainType === 'main',
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

/**
 * Hook to get organization branding styles
 */
export const useTenantBranding = () => {
  const { tenant } = useTenant();

  return {
    primaryColor: tenant?.primaryColor || '#2563eb',
    secondaryColor: tenant?.secondaryColor || '#1e40af',
    logoUrl: tenant?.logoUrl,
    orgName: tenant?.orgName || 'ePeople Agent',
  };
};

/**
 * Hook to check tenant features
 */
export const useTenantFeatures = () => {
  const { tenant } = useTenant();

  return {
    hasPublicJobBoard: tenant?.features.publicJobBoard ?? false,
    hasCareerPage: tenant?.features.careerPage ?? false,
    hasApplicationForm: tenant?.features.applicationForm ?? false,
    hasATSIntegration: tenant?.features.atsIntegration ?? false,
  };
};

/**
 * Get tenant-aware URLs
 */
export const useTenantUrls = () => {
  const { tenant, isTenantDomain } = useTenant();

  const getUrl = (path: string) => {
    if (isTenantDomain && tenant) {
      return `https://${tenant.subdomain}.epeople.work${path}`;
    }
    return path;
  };

  return {
    careersUrl: getUrl('/careers'),
    jobsUrl: getUrl('/jobs'),
    applyUrl: (jobId: string) => getUrl(`/jobs/${jobId}/apply`),
    homeUrl: getUrl('/'),
  };
};
